import {createContext, FC, useContext, useEffect, useState, type ReactNode} from "react"
import * as amplitude from "@amplitude/analytics-browser"
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser"
import {Experiment, ExperimentClient} from "@amplitude/experiment-js-client"
import type {ValidPropertyType} from "@amplitude/analytics-core/lib/esm/identify"

import {AMPLITUDE_API_KEY, AMPLITUDE_EXPERIMENTS_DEPLOY_KEY} from "../../env"

import "./types"

interface AnalyticsContextType {
	isAnalyticsEnabled: boolean
	track: (eventName: string, payload?: any) => void
	setUserId: (userId: string) => void
	setUserProperties: (properties: any) => void
	variant: (flagKey: string, fallback?: string) => string
	revenue: (
		amount: number,
		currency: string,
		revenueType: string,
		eventProperties?: Record<string, ValidPropertyType>,
	) => void
}

const AnalyticsContext = createContext<AnalyticsContextType>({
	isAnalyticsEnabled: false,
	track: () => {},
	setUserId: () => {},
	setUserProperties: () => {},
	variant: () => "",
	revenue: () => {},
})

const useAnalytics = () => useContext(AnalyticsContext)

interface AnalyticsProviderProps {
	children: ReactNode
}

let isAnalyticsInitialized = false

const AnalyticsProvider: FC<AnalyticsProviderProps> = ({children}) => {
	const [isAnalyticsEnabled, setIsAnalyticsEnabled] = useState(false)
	const [isConsentGiven, setIsConsentGiven] = useState(false)
	const [experimentClient, setExperimentClient] = useState<ExperimentClient | null>(null)

	useEffect(() => {
		const checkConsentAndInit = () => {
			if (window.cookiehub?.hasAnswered() && window.cookiehub?.hasConsented("analytics")) {
				initializeAnalytics()
				setIsConsentGiven(true)
			} else {
				setIsConsentGiven(false)
			}
		}

		// Function to initialize Amplitude with Session Replay
		const initializeAnalytics = async () => {
			try {
				if (!isAnalyticsInitialized) {
					isAnalyticsInitialized = true
					// Create session replay plugin with configuration
					const sessionReplayTracking = sessionReplayPlugin({
						sampleRate: 0.1, // 10% sample rate - adjust as needed
						debugMode: false, // Set to true for debugging
					})

					// Add the session replay plugin before initializing
					amplitude.add(sessionReplayTracking)

					// Initialize Amplitude with configuration
					amplitude.init(AMPLITUDE_API_KEY, undefined, {
						logLevel: 0,
						defaultTracking: {
							sessions: true,
						},
					})

					const experiment = Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_EXPERIMENTS_DEPLOY_KEY)
					try {
						await experiment.fetch()
					} catch (error: any) {
						console.error("Failed to fetch experiment data:", error.message)
					}

					setExperimentClient(experiment)
					setIsAnalyticsEnabled(true)
				}
			} catch (error: any) {
				console.error("Failed to initialize analytics:", error.message)
			}
		}

		// Check initial consent state
		checkConsentAndInit()

		// Listen for CookieHub consent events
		const handleConsentEvent = () => {
			checkConsentAndInit()
		}

		// Add event listeners for CookieHub events
		window.addEventListener("cookiehub:consent-saved", handleConsentEvent)
		window.addEventListener("cookiehub:consent-changed", handleConsentEvent)

		// Cleanup event listeners
		return () => {
			window.removeEventListener("cookiehub:consent-saved", handleConsentEvent)
			window.removeEventListener("cookiehub:consent-changed", handleConsentEvent)
		}
	}, [])

	const track = (eventName: string, payload?: any) => {
		if (isAnalyticsEnabled && isAnalyticsInitialized) {
			amplitude.track(eventName, payload)
		}
	}

	const variant = (flagKey: string, fallback: string = "") => {
		if (isAnalyticsEnabled && isConsentGiven && isAnalyticsInitialized && experimentClient) {
			return experimentClient.variant(flagKey, fallback).value ?? fallback
		}
		return fallback
	}

	const setUserId = (userId: string) => {
		if (isAnalyticsEnabled && isAnalyticsInitialized) {
			amplitude.setUserId(userId)
		}
	}

	const setUserProperties = (properties: Record<string, ValidPropertyType>) => {
		if (isAnalyticsEnabled && isAnalyticsInitialized) {
			const identifyEvent = new amplitude.Identify()
			for (const [key, value] of Object.entries(properties)) {
				identifyEvent.set(key, value)
			}
			amplitude.identify(identifyEvent)
		}
	}

	const revenue = (
		amount: number,
		currency: string,
		revenueType: string,
		eventProperties?: Record<string, ValidPropertyType>,
	) => {
		if (isAnalyticsEnabled && isAnalyticsInitialized) {
			const event = new amplitude.Revenue().setPrice(amount).setCurrency(currency).setRevenueType(revenueType)

			if (eventProperties) {
				event.setEventProperties(eventProperties)
			}

			amplitude.revenue(event)
		}
	}

	return (
		<AnalyticsContext.Provider value={{isAnalyticsEnabled, track, variant, setUserId, setUserProperties, revenue}}>
			{children}
		</AnalyticsContext.Provider>
	)
}

export {useAnalytics, AnalyticsProvider}
